import { computed, reactive, ref, readonly } from "vue";

import eventHub from "@/helpers/utility/Events";
import * as API from "@/api/API";
import Compressor from "@/helpers/data/Compressor";
import type { ModelVersionMeta } from "@/api/ModelVersion";

import {
  ModeldataNotFoundError,
  ModelNotFoundError,
  NotFoundError
} from "@core/errors/Errors";
import { cloudMediaAPI } from "@core/media/media.store";

import type { SharedModel } from "./SharedModel";
import type { User } from "./User";

const sharedModels = reactive<{ [id: number]: SharedModel }>({});
const loadingIds = ref(new Set<number>());

const nonArchivedSharedModels = computed(() =>
  Object.values(sharedModels).filter((model) => !model.archived)
);
const archivedSharedModels = computed(() =>
  Object.values(sharedModels).filter((model) => model.archived)
);

async function loadSharedModels(requestOptions = {}) {
  const models = await API.get<SharedModel[]>(
    "/v1/shared-models",
    requestOptions
  );
  if (!models) return [];

  for (const model of models) {
    sharedModels[model.id] = model;
  }

  return models;
}

export function useSharedModels(props?: { shareId: number }) {
  const sharedModelsCount = computed(() => Object.values(sharedModels).length);

  const isLoading = computed(() =>
    props ? loadingIds.value.has(props.shareId) : false
  );

  const sharedModel = computed(() =>
    props ? sharedModels?.[props.shareId] : undefined
  );

  async function getSharedModelInfo(shareId: number, requestOptions = {}) {
    loadingIds.value.add(shareId);
    try {
      const share = await API.get<SharedModel>(
        `/v1/shared-models/${shareId}`,
        requestOptions
      );

      sharedModels[share.id] = share;

      return readonly(sharedModels)[share.id];
    } finally {
      loadingIds.value.delete(shareId);
    }
  }

  /**
   * Get and decompress the main content of a shared simcision model.
   */
  async function getSharedModelData(shareId: number, requestOptions = {}) {
    try {
      const modelDataMetaPromise = API.get<ModelVersionMeta>(
        `/v1/shared-models/${shareId}/modeldata/meta`,
        { ...requestOptions }
      );
      const modelDataPromise = API.get<string>(
        `/v1/shared-models/${shareId}/modeldata`,
        {
          ...requestOptions,
          responseType: "text",
          parseResponse: (response) => {
            return Compressor.decompress(response.text);
          }
        }
      );
      const [modelData, metaData] = await Promise.all([
        modelDataPromise,
        modelDataMetaPromise
      ]);

      // load all model media files
      const mediaEntries = await cloudMediaAPI
        .loadModelMedia(metaData.media, requestOptions)
        .catch((error) => {
          console.warn(error);
          return [];
        });

      return {
        id: metaData.id,
        data: modelData,
        media: mediaEntries
      };
    } catch (error) {
      if (error instanceof NotFoundError) {
        throw new ModeldataNotFoundError(error.message);
      }
      throw error;
    }
  }

  async function copySharedModel(
    share: { id: number },
    copyData: { workspaceId: number, folderId: number, name: string }
  ) {
    const shareId = share.id;

    try {
      return await API.post<SharedModel>(`/v1/shared-models/${shareId}/copy`, {
        data: copyData
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        throw new ModelNotFoundError(error.message);
      }
      throw error;
    }
  }

  async function archiveSharedModel(id: number) {
    const share = await API.patch<SharedModel>(`/v1/shared-models/${id}`, {
      data: { archived: new Date().toISOString() }
    });

    sharedModels[share.id] = share;

    return share;
  }
  async function unArchiveSharedModel(id: number) {
    const share = await API.patch<SharedModel>(`/v1/shared-models/${id}`, {
      data: { archived: null }
    });

    sharedModels[share.id] = share;

    return share;
  }

  async function deleteSharedModel(id: number) {
    await API.destroy(`/v1/shared-models/${id}`);
    delete sharedModels[id];
  }

  return {
    sharedModel,
    sharedModels,
    sharedModelsCount,

    nonArchivedSharedModels,
    archivedSharedModels,

    loadSharedModels,
    getSharedModelInfo,
    getSharedModelData,
    deleteSharedModel,

    copySharedModel,
    archiveSharedModel,
    unArchiveSharedModel,

    isLoading
  };
}

function reset() {
  for (const shareId in sharedModels) {
    delete sharedModels[shareId];
  }
}

eventHub.on("user-changed", (user: User | null) => {
  console.log("%cresetting shared models", "color: lightgrey;");
  reset();
});
